import React, {useState} from 'react';
import Avatar from '../Avatar';
import { useParticipantNetworkQualityLevel } from '../../hooks/useParticipantNetworkQualityLevel';
import NetworkQualityLevel from '../NetworkQualityLevel';
import usePublications from '../../hooks/usePublications';
import useTrack from '../../hooks/useTrack';
import useIsTrackEnabled from '../../hooks/useIsTrackEnabled';
import useIsTrackSwitchedOff from '../../hooks/useIsTrackSwitchedOff';
import useIsTrackContentPreferences from '../../hooks/useIsTrackContentPreferences';
import { PROVIDER } from '../../constants';
import { useTwilioAppState } from '../../state';
import ParticipantOverlay from './ParticipantOverlay';
import useLongPress from '../../hooks/useLongPress';
import MainParticipantOverlay from '../MainParticipantOverlay';
import { isMobile } from '../../utils';
import YesNoComponent from '../YesNoComponent';
import useVideoContext from '../../hooks/useVideoContext';

const ParticipantInfo = ({ participant, isLocalParticipant, onClick, isSelected, children}) => {
    const networkQualityLevel = useParticipantNetworkQualityLevel(participant, isLocalParticipant ? true : false);    
    const participantName = participant && participant.identity && participant.identity.split("||") && participant.identity.split("||").length >= 3 && participant.identity.split("||")[3] || "";
    const participantType = participant && participant.identity && participant.identity.split("||") && participant.identity.split("||").length >= 2 && participant.identity.split("||")[2] || "";
    const publications = usePublications(participant);
    const audioPublication = publications.find(p => p.kind === 'audio');
    const audioTrack = useTrack(audioPublication); 
    const isTrackEnabled = useIsTrackEnabled(audioTrack);
    const showTwilioNetworkIndicator = window.serverData.data.showTwilioNetworkIndicator;
    const videoPublication = publications.find(p => p.trackName.includes('camera'));
    const isVideoEnabled = Boolean(videoPublication);
    const videoTrack = useTrack(videoPublication);
    const isVideoSwitchedOff = useIsTrackSwitchedOff(videoTrack);
    const [participantHovered, setParticipantHovered] = useState(false);
    const [isLongPress, setIsLongPressed] = useState(false);
    const abilityToTurnoffVideoorAudioForParticipants = window.serverData.data.abilityToTurnoffVideoorAudioForParticipants;
    const { kickoutGuest } = useVideoContext();  
    const [showRemoveGuestModal, setShowRemoveGuestModal] = useState(false);
    const isTrackContentPreferences= useIsTrackContentPreferences(videoTrack);
   
    const { source, localParticipantRole } = useTwilioAppState();
    const defaultOptions = {
        shouldPreventDefault: true,
        delay: 500,
    };

    const onLongPress = (event) => {
        if (!isLocalParticipant) {
            console.log("longpress is triggered");
            console.log({ event });
            setParticipantHovered(false);
            setIsLongPressed(true);
        }
    };

    const longPressEvent = useLongPress(onLongPress, onClick, defaultOptions);
    const removeGuestModalText = `<h3>Are you sure you want to remove <span class="kick-out-name">${participantName}</span>?</h3>`; 

    const onYesClick = () => {      
        setShowRemoveGuestModal(false);
        if (source === PROVIDER) {
            kickoutGuest(participant.identity);
        }
    }

    const onNoClick = () => {    
        setShowRemoveGuestModal(false);
    }

    return (
        <React.Fragment>
            <div
                className={`participant-info-container ${showTwilioNetworkIndicator && networkQualityLevel ? "show-network" : ""}`}
                onClick={onClick}
                data-cy-participant={participant.identity}
                onMouseEnter={() => setParticipantHovered(true)}           
                {...longPressEvent}
                onMouseLeave={isLongPress ? longPressEvent.onMouseLeave : () => setParticipantHovered(false)}
                id="ParticipantInfoContainer"
            >            
                {(!isVideoEnabled || isVideoSwitchedOff) && <Avatar userName={participantName} />}
                {children}

                {abilityToTurnoffVideoorAudioForParticipants && !isLocalParticipant && source === PROVIDER && participantHovered && !isLongPress &&
                    <ParticipantOverlay identity={participant.identity} isAudioEnabled={isTrackEnabled} isVideoEnabled={isVideoEnabled} setShowRemoveGuestModal={setShowRemoveGuestModal} />
                }

                <span className="participant-name"> {showTwilioNetworkIndicator && <NetworkQualityLevel qualityLevel={networkQualityLevel} />}
                    <div className="participant-name-label">{participantName}</div>
                </span>            
                {!isTrackEnabled && <span className="icon-mic-off" />}
            </div>

            {abilityToTurnoffVideoorAudioForParticipants && isMobile && !isLocalParticipant && source === PROVIDER && !participantHovered && isLongPress  &&
                <MainParticipantOverlay identity={participant.identity} participantName={participantName} participantType={participantType} isAudioEnabled={isTrackEnabled} isVideoEnabled={isVideoEnabled} target="ParticipantInfoContainer" toggle={() => setIsLongPressed(false)} setShowRemoveGuestModal={setShowRemoveGuestModal} />
            }
            <YesNoComponent
            showModal={showRemoveGuestModal}
            className="kick-out-modal"
            modalText={removeGuestModalText}
            modalYesFunction={onYesClick}
            modalNoFunction={onNoClick}
        />
        </React.Fragment>
    );
}

export default ParticipantInfo;
